// ===== 1. Imports =====
import React, { useEffect, useState, useMemo } from "react";
import PageHeader from "../components/PageHeader";
import {
  FileText,
  FileSpreadsheet,
  FileBarChart,
  FileArchive,
} from "lucide-react";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import { db } from "../firebaseConfig";
import { collection, getDocs, query, orderBy } from "firebase/firestore";

// ===== 2. Componente Principal =====
export default function ReportsPage() {
  // ===== 3. Sesión =====
  const session = useMemo(() => {
    try {
      return JSON.parse(sessionStorage.getItem("filma_session") || "{}");
    } catch {
      return {};
    }
  }, []);
  const projectCode = session.projectCode || session.project || "";
  const [loading, setLoading] = useState(false);

  // ===== 4. Lista de Informes =====
  const reports = [
    { id: "PO", title: "Informe de POs", icon: FileText },
    { id: "PO_ITEMS", title: "Informe de ítems PO", icon: FileSpreadsheet },
    { id: "FACTURAS", title: "Informe de facturas", icon: FileBarChart },
    { id: "PRESUPUESTO", title: "Presupuesto", icon: FileArchive },
  ];

  // ===== 5. Descargar Informes =====
  const handleDownload = async (id) => {
    if (!projectCode) {
      alert("No se ha encontrado el proyecto activo en la sesión.");
      return;
    }
    setLoading(true);
    try {
      if (id === "PRESUPUESTO") await exportBudget();
      else if (id === "PO") await exportPOs();
      else if (id === "PO_ITEMS") await exportPOItems();
      else if (id === "FACTURAS") await exportInvoices();
    } catch (err) {
      console.error(err);
      alert("Error generando el informe: " + err.message);
    }
    setLoading(false);
  };

  // ===== 6. Estilo de Celdas =====
  const styleCell = (ws) => {
    const range = XLSX.utils.decode_range(ws["!ref"]);
    for (let R = range.s.r; R <= range.e.r; R++) {
      for (let C = range.s.c; C <= range.e.c; C++) {
        const cellRef = XLSX.utils.encode_cell({ r: R, c: C });
        if (!ws[cellRef]) continue;
        ws[cellRef].s = {
          font: {
            bold: R === 0,
            color: { rgb: R === 0 ? "FFFFFF" : "000000" },
          },
          fill: { fgColor: { rgb: R === 0 ? "4F81BD" : "FFFFFF" } },
          alignment: {
            vertical: "center",
            horizontal: R === 0 ? "center" : "left",
          },
          border: {
            top: { style: "thin", color: { rgb: "CCCCCC" } },
            bottom: { style: "thin", color: { rgb: "CCCCCC" } },
            left: { style: "thin", color: { rgb: "CCCCCC" } },
            right: { style: "thin", color: { rgb: "CCCCCC" } },
          },
        };
      }
    }
  };

  // ===== 7. Exportar Presupuesto =====
  const exportBudget = async () => {
    const snap = await getDocs(
      collection(db, "projects", projectCode, "budget")
    );
    const rows = [];
    snap.forEach((doc) => {
      const d = doc.data();
      if (Array.isArray(d.subaccounts)) {
        d.subaccounts.forEach((s) => {
          rows.push({
            Código: `${d.code || ""}.${s.code || ""}`,
            Nombre: s.name || "",
            "Presupuesto (€)": Number(s.budget || 0).toLocaleString("es-ES"),
          });
        });
      } else {
        rows.push({
          Código: d.code || "",
          Nombre: d.name || "",
          "Presupuesto (€)": Number(d.budget || 0).toLocaleString("es-ES"),
        });
      }
    });
    const ws = XLSX.utils.json_to_sheet(rows);
    ws["!cols"] = [{ wch: 15 }, { wch: 40 }, { wch: 20 }];
    styleCell(ws);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Presupuesto");
    const wbout = XLSX.write(wb, { bookType: "xlsx", type: "array" });
    saveAs(
      new Blob([wbout], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      }),
      `Presupuesto_${projectCode}.xlsx`
    );
  };

  // ===== 8. Exportar POs =====
  const exportPOs = async () => {
    const qPOs = query(
      collection(db, "projects", projectCode, "pos"),
      orderBy("createdAt", "asc")
    );
    const snap = await getDocs(qPOs);
    const rows = [];
    snap.forEach((doc) => {
      const p = doc.data();
      rows.push({
        Número: p.number,
        Proveedor: p.supplier,
        Departamento: p.department,
        Descripción: p.description,
        Estado: p.status,
        "Creado por": p.createdBy,
        "Fecha creación": p.createdAt?.toDate
          ? p.createdAt.toDate().toLocaleString()
          : "",
      });
    });
    const ws = XLSX.utils.json_to_sheet(rows);
    ws["!cols"] = [
      { wch: 15 },
      { wch: 30 },
      { wch: 20 },
      { wch: 50 },
      { wch: 15 },
      { wch: 25 },
      { wch: 25 },
    ];
    styleCell(ws);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "POs");
    const wbout = XLSX.write(wb, { bookType: "xlsx", type: "array" });
    saveAs(
      new Blob([wbout], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      }),
      `POs_${projectCode}.xlsx`
    );
  };

  // ===== 9. Exportar Ítems de POs =====
  const exportPOItems = async () => {
    const qPOs = query(
      collection(db, "projects", projectCode, "pos"),
      orderBy("createdAt", "asc")
    );
    const snap = await getDocs(qPOs);
    const rows = [];
    snap.forEach((doc) => {
      const p = doc.data();
      (p.items || []).forEach((item, idx) => {
        rows.push({
          "Nº PO": p.number,
          Departamento: p.department,
          Proveedor: p.supplier,
          "Ítem #": idx + 1,
          "Descripción ítem": item.description,
          "Código presupuesto": item.subaccount,
          Base: item.base,
          IVA: item.iva,
          IRPF: item.irpf,
        });
      });
    });
    const ws = XLSX.utils.json_to_sheet(rows);
    ws["!cols"] = [
      { wch: 15 },
      { wch: 20 },
      { wch: 25 },
      { wch: 10 },
      { wch: 45 },
      { wch: 20 },
      { wch: 12 },
      { wch: 10 },
      { wch: 10 },
    ];
    styleCell(ws);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Items_PO");
    const wbout = XLSX.write(wb, { bookType: "xlsx", type: "array" });
    saveAs(
      new Blob([wbout], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      }),
      `Items_PO_${projectCode}.xlsx`
    );
  };

  // ===== 10. Exportar Facturas =====
  const exportInvoices = async () => {
    const qPOs = query(
      collection(db, "projects", projectCode, "pos"),
      orderBy("createdAt", "asc")
    );
    const snap = await getDocs(qPOs);
    const rows = [];
    for (const docSnap of snap.docs) {
      const po = docSnap.data();
      const invSnap = await getDocs(
        collection(db, "projects", projectCode, "pos", docSnap.id, "invoices")
      );
      invSnap.forEach((inv) => {
        const i = inv.data();
        rows.push({
          "Nº PO": po.number,
          "Factura ID": inv.id,
          Proveedor: i.supplier || po.supplier,
          Descripción: i.description,
          "Importe base": i.baseAmount || 0,
          "IVA (%)": i.taxPercent || 0,
          "Total factura": i.totalAmount || 0,
          "Fecha subida": i.createdAt?.toDate
            ? i.createdAt.toDate().toLocaleString()
            : "",
          "Subida por": i.createdBy,
        });
      });
    }
    const ws = XLSX.utils.json_to_sheet(rows);
    ws["!cols"] = [
      { wch: 15 },
      { wch: 20 },
      { wch: 25 },
      { wch: 40 },
      { wch: 15 },
      { wch: 10 },
      { wch: 15 },
      { wch: 25 },
      { wch: 20 },
    ];
    styleCell(ws);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Facturas");
    const wbout = XLSX.write(wb, { bookType: "xlsx", type: "array" });
    saveAs(
      new Blob([wbout], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      }),
      `Facturas_${projectCode}.xlsx`
    );
  };

  // ===== 11. Render =====
  return (
    <section
      className="container"
      style={{ paddingTop: 40, paddingBottom: 60 }}
    >
      <PageHeader title="Informes" />
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))",
          gap: 20,
          marginTop: 24,
        }}
      >
        {reports.map(({ id, title, icon: Icon }) => (
          <div
            key={id}
            className="card"
            style={{
              padding: 20,
              borderRadius: 14,
              boxShadow: "0 2px 5px rgba(0,0,0,0.08)",
              display: "flex",
              flexDirection: "column",
              justifyContent: "space-between",
              opacity: loading ? 0.6 : 1,
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
              <Icon size={28} color="var(--accent)" />
              <h3 style={{ margin: 0, fontSize: 18 }}>{title}</h3>
            </div>
            <button
              className="button button--primary"
              style={{
                marginTop: 16,
                alignSelf: "flex-start",
                cursor: loading ? "not-allowed" : "pointer",
              }}
              onClick={() => !loading && handleDownload(id)}
            >
              {loading ? "Generando..." : "Descargar"}
            </button>
          </div>
        ))}
      </div>
    </section>
  );
}
